import { FormControl, Grid, IconButton, InputAdornment, InputLabel, MenuItem, Select, TextField, Toolbar } from '@mui/material';

import React from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { debounce } from '../utils';
import { makeStyles } from '@mui/styles';
import { DataAPIContext, GET_LIST } from '../api';

const useToolbarStyles = makeStyles((theme) => ({
    toolbarRoot: {
        marginBottom: 0,
        height: '8vh',
    },
    hidden: {
        visibility: 'hidden',
    },
    grow: {
        flexGrow: 1,
    },
}));

export const UserChatHistoryToolbar = (props) => {
    const searchQueryRef = React.useRef(null);
    const classes = useToolbarStyles();
    const [, setSelectedGroupTourOption] = React.useState('');
    const dataAPI = React.useContext(DataAPIContext);
    const [tourUsers, setTourUsers] = React.useState([]);
    const [loadingTourUsers, setLoadingTourUsers] = React.useState(false);

    // Fetch tour_user data instead of using deprecated guest_trips
    React.useEffect(() => {
        if (!props.user?.id) {
            return;
        }

        let active = true;
        setLoadingTourUsers(true);

        (async () => {
            try {
                const params = {
                    filter: {
                        filters: JSON.stringify([{ field: 'user_ids', value: String(props.user.id) }]),
                    },
                    pagination: { page: 1, perPage: 1000 }, // Get all tour users for this user
                };
                const res = await dataAPI(GET_LIST, 'tour_user', params);
                if (!active) {
                    return;
                }
                if (res.statusCode === 200 && res.data) {
                    console.log('UserChatHistoryToolbar: Fetched tour_user data:', res.data);
                    setTourUsers(res.data);
                }
            } catch (error) {
                console.error('UserChatHistoryToolbar: Error fetching tour_user data:', error);
            } finally {
                if (active) {
                    setLoadingTourUsers(false);
                }
            }
        })();

        return () => {
            active = false;
        };
    }, [props.user?.id, dataAPI]);

    const handleSearchQueryChange = (ev) => {
        debouncedSearchQueryChangeHandler.cancel();
        doSearch(searchQueryRef.current?.value);
    };

    const debouncedSearchQueryChangeHandler = debounce(handleSearchQueryChange, 300);

    const doSearch = (query) => props.onQueryChange && props.onQueryChange(query);

    const handleGroupTourSelectChange = (event) => {
        setSelectedGroupTourOption(event.target.value);
        if (event.target.value === null) {
            props.setChatAPIFilters([{ field: 'timestamp', sort: props.chatListSortOrder }]);
        } else {
            props.setChatAPIFilters([
                { field: 'group_tour_chat_channel', op: 'equals', value: String(event.target.value) },
                { field: 'timestamp', sort: props.chatListSortOrder },
            ]);
        }
        props.setChatSelectedTourFilter(event.target.value);
    };

    const handleSortOrderSelectChange = (event) => {
        props.setChatListSortOrder(event.target.value);

        if (props.chatSelectedTourFilter === null) {
            props.setChatAPIFilters([{ field: 'timestamp', sort: event.target.value }]);
        } else {
            props.setChatAPIFilters([
                { field: 'group_tour_chat_channel', op: 'equals', value: String(props.chatSelectedTourFilter) },
                { field: 'timestamp', sort: event.target.value },
            ]);
        }
    };

    console.log('UserChatHistoryToolbar', props);

    return (
        <Toolbar disableGutters={true} style={{marginBottom: 0, height: !props.isSmallScreen ? '8vh' : null}}>
            <Grid container direction="row">
                <Grid item xs={12} sm={4} style={{}}>
                    <TextField
                        variant="filled"
                        label="Search"
                        size="small"
                        onChange={debouncedSearchQueryChangeHandler}
                        inputRef={searchQueryRef}
                        defaultValue={props.channelListQuery}
                        fullWidth
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton aria-label="do search" onClick={handleSearchQueryChange}>
                                        <SearchIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                {/* CP-8250: the 20px gutters only separate the three fields when they sit
                    side by side. Once they stack (xs) the gutter indents "Order by" out of
                    line with Search and Select Tour. */}
                <Grid item xs={12} sm={4} style={{ paddingLeft: props.isSmallScreen ? 0 : '20px', paddingRight: props.isSmallScreen ? 0 : '20px' }}>
                    <FormControl variant="filled" size="small" fullWidth className={classes.formControl}>
                        <InputLabel htmlFor="order-select-element">Order by</InputLabel>
                        <Select
                            //value={props.user.trips.roles}
                            onChange={handleSortOrderSelectChange}
                            //inputProps={{ name: 'userRoles', id: 'user-roles-select-element' }}
                            style={{ textTransform: 'capitalize' }}
                            value={props.chatListSortOrder}
                        >
                            <MenuItem key={0} value={'desc'} style={{}}>
                                {'Most recent on top'}
                            </MenuItem>
                            <MenuItem key={1} value={'asc'} style={{}}>
                                {'Earliest on top'}
                            </MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                {props.user.roles == null || props.user.roles.length === 0 ? (
                    <Grid item xs={12} sm={4} style={{ marginLeft: 'auto', marginRight: 0 }}>
                        <FormControl variant="filled" size="small" fullWidth className={classes.formControl}>
                            <InputLabel htmlFor="user-roles-select-element">Select Tour</InputLabel>
                            <Select
                                //value={props.user.trips.roles}
                                onChange={handleGroupTourSelectChange}
                                //inputProps={{ name: 'userRoles', id: 'user-roles-select-element' }}
                                style={{ textTransform: 'capitalize' }}
                                defaultValue={props.chatSelectedTourFilter}
                                MenuProps={{ PaperProps: { sx: { maxHeight: 400 } } }}
                            >
                                <MenuItem key={-1} value={null} style={{ textTransform: 'capitalize', color: 'gray' }}>
                                    {'Select tour'}
                                </MenuItem>
                                {loadingTourUsers ? (
                                    <MenuItem disabled>Loading tours...</MenuItem>
                                ) : (
                                    tourUsers.map((tourUser, idx) => {
                                        // Get group_tour_chat_channel from tour_user or tour object
                                        const groupTourChatChannel = tourUser.group_tour_chat_channel || tourUser.tour?.group_tour_chat_channel;
                                        const tourName = tourUser.tour?.name || tourUser.tour_name || 'Unknown Tour';
                                        const travelDate = tourUser.date || tourUser.start_datetime || '';
                                        const departure = tourUser.departure?.name || tourUser.departure || '';
                                        
                                        return (
                                            <MenuItem 
                                                key={tourUser.id} 
                                                value={groupTourChatChannel || tourUser.id} 
                                                style={{ textTransform: 'capitalize' }} 
                                                selected={props.chatSelectedTourFilter === groupTourChatChannel}
                                            >
                                                {tourName}&nbsp;&nbsp;&nbsp;
                                                <span style={{ color: 'gray', fontSize: '0.75rem', marginLeft: 'auto', marginRight: 0 }}>
                                                    {(travelDate ? ' ' + travelDate : '') + (departure ? ' ' + departure : '')}
                                                </span>
                                            </MenuItem>
                                        );
                                    })
                                )}
                            </Select>
                        </FormControl>
                    </Grid>
                ) : (
                    ''
                )}
            </Grid>
        </Toolbar>
    );
};

export default UserChatHistoryToolbar;
